// Pure normalization for HQ usage events. Dynamic/content-identifying route
// segments are collapsed before persistence so path and section cardinality
// stay bounded as the desktop app grows.

export type HqUsageTab =
  | "home"
  | "jobs"
  | "inbox"
  | "outlets"
  | "giving"
  | "knowledge"
  | "help"
  | "me"
  | "marketing"
  | "equipment-purchase"
  | "tasks"
  | "spend"
  | "schedules"
  | "people"
  | "space"
  | "intake"
  | "other";

const SECTION_TABS = new Set<HqUsageTab>([
  "jobs",
  "inbox",
  "outlets",
  "giving",
  "knowledge",
  "help",
  "me",
  "marketing",
  "equipment-purchase",
  "tasks",
  "spend",
  "schedules",
  "people",
  "space",
]);

/** Params whose change represents a UI-state view rather than query noise.
 *  `view` = jobs board mode toggle; `month` = home calendar month flip. These
 *  are the only UI-state params /hq actually puts in the URL today — filters
 *  (outlet, entity, attention, person, ...) are deliberately excluded. */
export const HQ_USAGE_STATE_PARAMS = ["view", "month"] as const;

const INTAKE_TYPES = [
  "campaign",
  "catering",
  "content",
  "creative",
  "customer-refund",
  "equipment-purchase",
  "equipment-service",
  "fitout-task",
  "giving",
  "hiring",
  "incident-report",
  "license-renewal",
  "maintenance",
  "marketing-materials",
  "merch",
  "non-conformance",
  "partnership",
  "photo-shoot",
  "popup",
  "pre-loi",
  "production-issue",
  "project",
  "promo",
  "qa-review",
  "quality-audit",
  "research",
  "spend",
  "staffing-shortfall",
  "store-status",
  "vendor-issue",
  "web-task",
] as const;

const INTAKE_PATTERNS = INTAKE_TYPES.map((type) => `/hq/${type}/new`);

/** Known static route patterns, longest-prefix first. */
const ROUTE_PATTERNS: readonly string[] = [
  ...INTAKE_PATTERNS,
  "/hq/marketing/assets",
  "/hq/equipment-purchase",
  "/hq/knowledge",
  "/hq/marketing",
  "/hq/schedules",
  "/hq/outlets",
  "/hq/giving",
  "/hq/people",
  "/hq/inbox",
  "/hq/tasks",
  "/hq/spend",
  "/hq/space",
  "/hq/jobs",
  "/hq/help",
  "/hq/me",
  "/hq/login",
  "/hq",
];

export function normalizeHqUsagePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "") || "/hq";

  const intake = INTAKE_PATTERNS.find(
    (pattern) => trimmed === pattern || trimmed.startsWith(`${pattern}/`),
  );
  if (intake) return intake;

  if (trimmed.startsWith("/hq/jobs/") && trimmed !== "/hq/jobs") {
    return "/hq/jobs/[id]";
  }
  if (trimmed.startsWith("/hq/outlets/") && trimmed !== "/hq/outlets") {
    return "/hq/outlets/[code]";
  }
  if (trimmed.startsWith("/hq/help/") && trimmed !== "/hq/help") {
    return "/hq/help/[slug]";
  }

  const match = ROUTE_PATTERNS.find(
    (pattern) => trimmed === pattern || trimmed.startsWith(`${pattern}/`),
  );
  return match ?? "/hq";
}

export function deriveHqTab(pathname: string): HqUsageTab {
  const trimmed = pathname.replace(/\/+$/, "") || "/hq";
  const normalized = normalizeHqUsagePath(pathname);
  if (trimmed === "/hq") return "home";
  if (normalized === "/hq") return "other";
  if (INTAKE_PATTERNS.includes(normalized)) return "intake";
  const segment = normalized.split("/")[2] ?? "";
  return SECTION_TABS.has(segment as HqUsageTab)
    ? (segment as HqUsageTab)
    : "other";
}

/** Persist only the allowlisted UI state, never filters or content IDs. */
export function deriveHqVariant(params: URLSearchParams): string | null {
  const kept = new URLSearchParams();
  for (const key of HQ_USAGE_STATE_PARAMS) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  return kept.size > 0 ? kept.toString() : null;
}
